import { PublicLayout } from '@/components/PublicLayout';
import { LogoMark, SectionEyebrow } from '@/components/ui';
import { LegalSection, LegalLink } from './MentionsLegalesPage';
import { FileText, BookOpen } from 'lucide-react';

/**
 * Conditions Générales d'Utilisation — Vision Pro Collectivité.
 *
 * Structure standard SaaS public adaptée au contexte collectivité :
 *   - Objet & champ d'application
 *   - Description du Service
 *   - Conditions d'accès (gratuit, majorité, mineurs via parent)
 *   - Compte et identifiants
 *   - Propriété intellectuelle
 *   - Obligations utilisateur (anti-signalements abusifs)
 *   - Disponibilité
 *   - Données personnelles (renvoi PC)
 *   - Résiliation
 *   - Limitation responsabilité
 *   - Modification CGU
 *   - Droit applicable & juridiction
 *
 * Refonte R12 (mai 2026) :
 *   - Header glass avec LogoMark BookOpen accent tech + eyebrow + titre Geist
 *   - 12 articles avec LegalSection helper (import depuis MentionsLegalesPage)
 *   - Liens internes /politique-confidentialite en LegalLink
 *   - Listes ul/ol avec marker:text-ink-subtle
 *   - Code inline en bg-surface/60 backdrop-blur-glass
 *   - Footer version document mono
 */
export function CguPage() {
  return (
    <PublicLayout maxWidth="lg" centered={false}>
      <article className="space-y-6 py-4 animate-fade-in">
        {/* Header */}
        <header className="flex items-start gap-3 pb-4 border-b border-border">
          <LogoMark icon={BookOpen} accent="tech" size="lg" />
          <div className="flex-1">
            <SectionEyebrow as="div">Service public local</SectionEyebrow>
            <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold tracking-r-tight text-ink">
              Conditions générales d'utilisation
            </h1>
            <p className="mt-2 text-sm text-ink-subtle leading-relaxed">
              Version <strong className="text-ink">1.0</strong> — Entrée en vigueur&nbsp;:{' '}
              1.0.
            </p>
          </div>
        </header>

        {/* ============ 1 ============ */}
        <LegalSection number="Art. 1" title="Objet">
          <p className="text-sm text-ink-muted leading-relaxed">
            Les présentes Conditions Générales d'Utilisation («&nbsp;CGU&nbsp;») régissent
            l'accès et l'utilisation du service Vision Pro Collectivité («&nbsp;le Service&nbsp;»),
            édité par One For All Distribution et mis à disposition de votre collectivité dans le cadre
            d'un contrat de prestation de service public local.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            L'utilisation du Service implique l'acceptation pleine et entière des présentes CGU.
            Si vous n'acceptez pas ces conditions, vous ne devez pas créer de compte ni utiliser
            le Service.
          </p>
        </LegalSection>

        {/* ============ 2 ============ */}
        <LegalSection number="Art. 2" title="Description du Service">
          <p className="text-sm text-ink-muted leading-relaxed">
            Vision Pro Collectivité est une application web et mobile permettant&nbsp;:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>
              Aux <strong className="text-ink">administrés</strong> de signaler des incidents
              sur la voie publique, de suivre l'état d'avancement de leurs demandes et de
              recevoir des informations municipales&nbsp;;
            </li>
            <li>
              Aux <strong className="text-ink">agents de la collectivité</strong> (rôles agent,
              manager, admin) de gérer les signalements, les communications, et les processus
              métiers de la mairie (selon les modules activés)&nbsp;;
            </li>
            <li>
              Aux <strong className="text-ink">élus</strong> de disposer d'un tableau de bord
              décisionnel.
            </li>
          </ul>
          <p className="text-sm text-ink-muted leading-relaxed">
            Le Service évolue. De nouvelles fonctionnalités peuvent être ajoutées par mise à jour
            sans modification des présentes CGU, dès lors qu'elles ne créent pas de nouvelle
            obligation pour l'utilisateur.
          </p>
        </LegalSection>

        {/* ============ 3 ============ */}
        <LegalSection number="Art. 3" title="Conditions d'accès">
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">3.1.</strong> L'inscription au Service en qualité
            d'administré est <strong className="text-ink">gratuite</strong>. Le Service est financé par votre
            collectivité.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">3.2.</strong> L'inscription autonome au Service en
            qualité d'administré est <strong className="text-ink">réservée aux personnes majeures</strong>{' '}
            (≥&nbsp;18&nbsp;ans). Les mineurs peuvent utiliser le Service via le compte d'un
            parent ou d'un représentant légal.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">3.3.</strong> Les comptes professionnels (rôles agent,
            manager, admin) sont créés par votre collectivité, qui en fixe les modalités d'accès
            et de retrait.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">3.4.</strong> L'utilisateur garantit que les
            informations fournies à l'inscription sont exactes, complètes et à jour. Il s'engage
            à les maintenir actualisées depuis son espace personnel.
          </p>
        </LegalSection>

        {/* ============ 4 ============ */}
        <LegalSection number="Art. 4" title="Compte et identifiants">
          <p className="text-sm text-ink-muted leading-relaxed">
            L'utilisateur est seul responsable de la confidentialité de ses identifiants. Il
            s'engage à&nbsp;:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>Choisir un mot de passe fort, unique au Service ;</li>
            <li>Ne pas communiquer son mot de passe à un tiers ;</li>
            <li>
              Activer la double authentification (2FA TOTP) lorsque cela est proposé,
              particulièrement pour les comptes professionnels ;
            </li>
            <li>
              Notifier immédiatement tout accès non autorisé soupçonné à son compte par email à{' '}
              <LegalLink href="mailto:contact@oneforall-distribution.fr">contact@oneforall-distribution.fr</LegalLink>.
            </li>
          </ul>
        </LegalSection>

        {/* ============ 5 ============ */}
        <LegalSection number="Art. 5" title="Propriété intellectuelle">
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">5.1.</strong> L'éditeur reste propriétaire de l'ensemble
            des droits de propriété intellectuelle afférents au Service (code source, interface,
            marques, logos).
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">5.2.</strong> L'utilisateur conserve la propriété des
            contenus qu'il téléverse (textes de signalement, photos, géolocalisations). Il
            concède à l'éditeur une licence non-exclusive, non transférable, mondiale, à titre
            gratuit, pour héberger et traiter ces contenus aux <strong className="text-ink">seules fins de fourniture
            du Service</strong>. Cette licence prend fin à la suppression du contenu ou du compte.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">5.3.</strong> L'utilisateur garantit qu'il détient tous
            les droits nécessaires sur les contenus qu'il publie (notamment droit à l'image des
            personnes éventuellement présentes sur les photos).
          </p>
        </LegalSection>

        {/* ============ 6 ============ */}
        <LegalSection number="Art. 6" title="Obligations de l'utilisateur">
          <p className="text-sm text-ink-muted leading-relaxed">L'utilisateur s'engage à&nbsp;:</p>
          <ul className="list-disc pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>
              Utiliser le Service de manière conforme à sa finalité et à la loi française ;
            </li>
            <li>
              Ne pas déposer de signalement abusif, diffamatoire, discriminatoire, raciste,
              haineux ou attentatoire à la vie privée de tiers ;
            </li>
            <li>
              Ne pas publier de contenu illicite, contrefait, ou portant atteinte aux droits de
              tiers ;
            </li>
            <li>
              Ne pas tenter d'accéder sans autorisation aux systèmes de l'éditeur, ne pas
              effectuer de rétro-ingénierie, de scan de vulnérabilités sans autorisation
              écrite ;
            </li>
            <li>
              Ne pas utiliser le Service à des fins commerciales ou pour démarcher d'autres
              utilisateurs ;
            </li>
            <li>
              Ne pas créer plusieurs comptes pour contourner les limitations du Service.
            </li>
          </ul>
          <p className="text-sm text-ink-muted leading-relaxed">
            Tout signalement abusif peut faire l'objet d'une suspension immédiate du compte et,
            le cas échéant, d'un signalement aux autorités compétentes.
          </p>
        </LegalSection>

        {/* ============ 7 ============ */}
        <LegalSection number="Art. 7" title="Disponibilité du Service">
          <p className="text-sm text-ink-muted leading-relaxed">
            L'éditeur met en œuvre les moyens raisonnables pour assurer la disponibilité du
            Service en mode <strong className="text-ink">best-effort 24h/24, 7j/7</strong>, hors maintenances
            programmées (annoncées avec un préavis de 48 heures lorsque possible) et causes
            extérieures (force majeure, panne réseau du fournisseur, attaques malveillantes).
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            Le Service étant fourni dans le cadre d'un service public local, aucune garantie de
            résultat n'est due aux utilisateurs au-delà des engagements contractuels passés
            entre l'éditeur et votre collectivité.
          </p>
        </LegalSection>

        {/* ============ 8 ============ */}
        <LegalSection number="Art. 8" title="Données personnelles">
          <p className="text-sm text-ink-muted leading-relaxed">
            Le traitement des données personnelles est régi par la{' '}
            <LegalLink href="/politique-confidentialite">Politique de confidentialité</LegalLink>,
            conforme au Règlement (UE) 2016/679 (RGPD) et à la loi n°&nbsp;78-17 du 6 janvier 1978 modifiée.
          </p>
        </LegalSection>

        {/* ============ 9 ============ */}
        <LegalSection number="Art. 9" title="Résiliation">
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">9.1.</strong> L'utilisateur peut résilier son compte à
            tout moment, sans préavis ni justification, depuis son espace personnel{' '}
            <code className="rounded-r-md bg-surface/60 backdrop-blur-glass px-1.5 py-0.5 font-mono text-xs">/administre/compte</code>
            . La suppression est effective dans un délai maximum de 30 jours, sauf obligations
            légales de conservation (cf. Politique de confidentialité §3).
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">9.2.</strong> L'éditeur se réserve le droit de
            suspendre ou résilier un compte en cas de violation grave des présentes CGU, après
            mise en demeure restée sans effet pendant 72 heures (sauf urgence ou caractère
            manifestement illégal du manquement).
          </p>
        </LegalSection>

        {/* ============ 10 ============ */}
        <LegalSection number="Art. 10" title="Limitation de responsabilité">
          <p className="text-sm text-ink-muted leading-relaxed">
            Dans les limites autorisées par la loi applicable, la responsabilité de l'éditeur ne
            peut être engagée pour les dommages indirects (perte d'opportunité, préjudice
            d'image, etc.), ni pour les conséquences d'une utilisation non conforme du Service
            ou d'une compromission du compte due à un manquement de l'utilisateur à ses propres
            obligations de sécurité.
          </p>
        </LegalSection>

        {/* ============ 11 ============ */}
        <LegalSection number="Art. 11" title="Modification des CGU">
          <p className="text-sm text-ink-muted leading-relaxed">
            L'éditeur se réserve le droit de modifier les présentes CGU pour s'adapter aux
            évolutions légales et techniques. Les modifications substantielles seront notifiées
            par email et l'utilisateur sera invité à ré-accepter la nouvelle version lors de sa
            prochaine connexion.
          </p>
        </LegalSection>

        {/* ============ 12 ============ */}
        <LegalSection number="Art. 12" title="Droit applicable et juridiction">
          <p className="text-sm text-ink-muted leading-relaxed">
            Les présentes CGU sont soumises au <strong className="text-ink">droit français</strong>.
            En cas de litige, et après tentative préalable de résolution amiable auprès du
            service technique de la mairie ou de l'éditeur (contact@oneforall-distribution.fr), le litige
            sera porté devant les juridictions françaises territorialement compétentes. Le Service
            étant destiné à des collectivités et non à des consommateurs, le dispositif de médiation
            de la consommation (article L.612-1) ne s'applique pas.
          </p>
        </LegalSection>

        <footer className="pt-4 border-t border-border text-xs text-ink-subtle">
          <FileText className="mr-1 inline h-3.5 w-3.5 align-text-bottom" aria-hidden="true" />
          Version&nbsp;: <strong className="text-ink">1.0</strong>.
        </footer>
      </article>
    </PublicLayout>
  );
}
